import React, { forwardRef, InputHTMLAttributes, useState } from 'react'
import InputMask from 'react-input-mask'
import { InputWrapperProps, InputContainer, InputWrapper, WrapperLight, ErrorContainer, IconContainer } from './styles'
import { IconName } from '~/assets/icons'
import { IconComponent } from '~/assets/icons/component'

export enum FormTestId {
  INPUT_MASK = 'input-mask',
  INPUT = 'input',
  SELECT_TEXT_COUNT = 'select-text-count-results',
  SELECT_OPTION_ERROR = 'select-option-error',
  SELECT_OPTION_ITEM = 'select-option-item',
  SELECT_INPUT_WRAPPER = 'select-input-wrapper',
  SELECT_INPUT = 'select-input',
  SELECT_ERROR = 'select-error',
  TABLE_LINE = 'table-line',
  TABLE_ERROR = 'table-error'
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement>, InputWrapperProps {
  icon?: IconName
  iconPosition?: 'left' | 'right'
  error?: boolean
  message?: string
  margin?: string
  mask?: string
  dataTestId?: string
  maskPlaceholder?: string
  disableTrim?: boolean
  onClickIcon?: () => void
  v7?: boolean
}
/**
 * Este componente irá renderizar um Input, estende HTMLInputElement props
 * @param {StringIcons} icon Exibe um ícone no lado direito do input
 * @param {string} iconPosition Propriedade usada para controlar a posição do ícone dentro do Input.
 * @param {boolean} error Necessário para quando exibir a mensagem de erro e a borda em vermelho.
 * @param {string} message Mensagem de erro.
 * @param {string} mask Máscara que irá formatar o texto.
 *
 * Ex: 999.999.999-99 irá formatar um CPF.
 * @param {string} maskPlaceholder Só irá ativar caso utilize a prop mask junto. Cria um placeholder de máscara.
 * @param {string} margin
 * @param {boolean} disableTrim Desabilita a formatação de trim que remove espaços a mais digitados no Input.
 * @param {boolean} v7 Propriedade mantida para não gerar conflito em projetos que a utilizam.
 *
 * Poderá ser removida em uma atualização futura.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      icon,
      iconPosition = 'right',
      error,
      message,
      margin,
      mask,
      value,
      maskPlaceholder,
      onChange,
      disableTrim,
      capture,
      onClickIcon,
      ...props
    }: InputProps,
    ref
  ) => {
    const [active, setActive] = useState(false)

    const trimEventValue = (event: React.ChangeEvent<HTMLInputElement>) => {
      event.target.value = event.target.value.trimStart().replace(/\s\s+/g, ' ')
    }

    return (
      <InputContainer margin={margin}>
        <WrapperLight
          direction={props.direction}
          error={error}
          active={active || undefined}
          iconPosition={iconPosition}
        >
          {mask ? (
            <InputMask
              {...props}
              value={value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setActive(true)
                !disableTrim && trimEventValue(event)
                onChange?.(event)
              }}
              onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                setActive(true)
                props.onFocus?.(event)
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                setActive(false)
                props.onBlur?.(event)
              }}
              maskPlaceholder={maskPlaceholder ?? null}
              mask={mask}
              alwaysShowMask
            >
              <InputWrapper
                data-testid={FormTestId.INPUT_MASK}
                ref={ref}
                errors={error}
                active={active}
                icon={icon}
                iconPosition={iconPosition}
              />
            </InputMask>
          ) : (
            <InputWrapper
              data-testid={FormTestId.INPUT}
              {...props}
              ref={ref}
              value={value}
              errors={error}
              active={active}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setActive(true)
                !disableTrim && trimEventValue(event)
                onChange?.(event)
              }}
              onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                setActive(true)
                props.onFocus?.(event)
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                setActive(false)
                props.onBlur?.(event)
              }}
              icon={icon}
              iconPosition={iconPosition}
            />
          )}
          {icon && (
            <IconContainer onClick={onClickIcon}>
              <IconComponent name={icon} />
            </IconContainer>
          )}
        </WrapperLight>
        {error && <ErrorContainer>{message}</ErrorContainer>}
      </InputContainer>
    )
  }
)

Input.displayName = 'Input'
